import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Grid,
  Page,
  PageRoute,
  TabContext,
  useElementBreakpoints,
  useParams,
  useUnsavedChanges,
  useValidationErrors
} from "@compass/core";
import { Contributor, ContributorViewResource, Permission } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ContributorBasicInfo } from "./ContributorBasicInfo";
import { ContributorPageHeader } from "./ContributorPageHeader";
import { ContributorTabs } from "./ContributorTabs";
import { Employments } from "./Employments/Employments";
import { Matches } from "./Matches/Matches";

const routeName = "contributor";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  tab?: ContributorTab | undefined = undefined;
}

export const contributorPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Contributor",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type ContributorTab = "Contributor" | "Time & Effort" | "Employments";

export const ContributorPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Contributor;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<ContributorTab>(optionalParams.tab || "Contributor");
  const [contributor, setContributor] = useState<Contributor>({} as Contributor);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [contributor] });
  const [viewResource, setViewResource] = useState<ContributorViewResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  // keeps track of which tabs data are loaded
  const [visitedTabs, setVisitedTabs] = useState<ContributorTab[]>([]);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource(params.id);
  }, []);

  // load data from each tab as it's selected
  useEffect(() => {
    if (!visitedTabs.includes(currentTab)) {
      setVisitedTabs(
        produce((draft) => {
          draft.push(currentTab);
        })
      );
    }
  }, [currentTab]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${contributorPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.contributors.get(id).then((response) => {
      setContributor(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.contributors.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <ContributorPageHeader
            rootPermission={ROOT_PERMISSION}
            crudMode={params.crudMode}
            fetch={fetch}
            reroute={reroute}
            setCurrentTab={setCurrentTab}
            contributor={contributor}
            viewResource={viewResource}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
          />
        }
        tabs={<ContributorTabs viewResource={viewResource} setCurrentTab={setCurrentTab} />}
      >
        <Grid ref={breakpointsRef} container spacing="md">
          <Grid item xs={setBreakpoints({ xs: 12 })}>
            <Grid hidden={currentTab !== "Contributor"}>
              <Grid container spacing="md">
                <Grid item xs={setBreakpoints({ xs: 12 })}>
                  <ContributorBasicInfo
                    contributor={contributor}
                    setContributor={setContributor}
                    viewResource={viewResource}
                    crudMode={params.crudMode}
                    validationErrors={validationErrors}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box mt="md" />
                </Grid>
              </Grid>
              <Grid container spacing="md">
                <Grid item xs={setBreakpoints({ xs: 12 })}>
                  <Employments
                    initialLoad={true}
                    contributorId={params.id}
                    onGetList={() => fetchViewResource(params.id)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box hidden={currentTab !== "Time & Effort"} height="100%" overflow="auto">
          <Matches
            initialLoad={visitedTabs.includes("Time & Effort")}
            contributorId={params.id}
            onGetList={() => fetchViewResource(params.id)}
          />
        </Box>
      </Page>
    </TabContext>
  );
};
